'use client'
import * as SelectPrimitive from '@radix-ui/react-select'
import { menuDataStateAnimationCls } from 'helpers'
import * as React from 'react'
import { cn } from 'utils/cn'

const Select = SelectPrimitive.Root

const SelectGroup = SelectPrimitive.Group

const SelectValue = SelectPrimitive.Value

const SelectIcon = SelectPrimitive.Icon

const SelectTrigger = React.forwardRef<
	React.ElementRef<typeof SelectPrimitive.Trigger>,
	React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
	<SelectPrimitive.Trigger
		ref={ref}
		className={cn(
			'ring-offset-background select-none text-dark-grey focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 data-[state=open]:text-black hover:text-black [&>span]:line-clamp-1',
			className,
		)}
		{...props}
	>
		{children}
	</SelectPrimitive.Trigger>
))

SelectTrigger.displayName = SelectPrimitive.Trigger.displayName

const SelectContent = React.forwardRef<
	React.ElementRef<typeof SelectPrimitive.Content>,
	React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = 'popper', ...props }, ref) => (
	<SelectPrimitive.Portal>
		<SelectPrimitive.Content
			ref={ref}
			className={cn(
				menuDataStateAnimationCls,
				'relative z-50 rounded-8 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
				className,
			)}
			position={position}
			{...props}
		>
			{children}
		</SelectPrimitive.Content>
	</SelectPrimitive.Portal>
))

SelectContent.displayName = SelectPrimitive.Content.displayName

const SelectLabel = React.forwardRef<
	React.ElementRef<typeof SelectPrimitive.Label>,
	React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
	<SelectPrimitive.Label
		ref={ref}
		className={cn('py-1.5 pl-8 pr-2', className)}
		{...props}
	/>
))

SelectLabel.displayName = SelectPrimitive.Label.displayName

const SelectItem = React.forwardRef<
	React.ElementRef<typeof SelectPrimitive.Item>,
	React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item> & {
		icon?: React.ReactNode
		leadingIcon?: React.ReactNode
	}
>(({ className, children, icon, leadingIcon, ...props }, ref) => (
	<SelectPrimitive.Item
		ref={ref}
		className={cn(
			'type-body-small relative mb-1 flex h-8 w-full cursor-pointer select-none items-center rounded-8 px-4 py-1 outline-none transition-colors focus:bg-purple-8 focus:text-blue data-[disabled]:pointer-events-none data-[state=checked]:bg-purple-8 data-[state=checked]:text-blue data-[disabled]:opacity-50 hover:bg-purple-8 hover:text-blue',
			className,
		)}
		{...props}
	>
		{leadingIcon && (
			<span className="mr-2 flex size-5 shrink-0 items-center justify-center">
				{leadingIcon}
			</span>
		)}
		<SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
		{icon && (
			<SelectPrimitive.ItemIndicator className="absolute right-4 top-1/2 -translate-y-1/2 text-blue">
				{icon}
			</SelectPrimitive.ItemIndicator>
		)}
	</SelectPrimitive.Item>
))

SelectItem.displayName = SelectPrimitive.Item.displayName

const SelectSeparator = React.forwardRef<
	React.ElementRef<typeof SelectPrimitive.Separator>,
	React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
	<SelectPrimitive.Separator
		ref={ref}
		className={cn('-mx-1 my-1 h-px', className)}
		{...props}
	/>
))

SelectSeparator.displayName = SelectPrimitive.Separator.displayName

export {
	Select,
	SelectContent,
	SelectGroup,
	SelectIcon,
	SelectItem,
	SelectLabel,
	SelectSeparator,
	SelectTrigger,
	SelectValue,
}
